import { FC, useState, useCallback } from 'react'
import { SortType } from 'rsuite-table'
import { Table, Toolbar, Modal, Button } from 'shared'
import { useTableHandlers } from 'hooks/useTableHandlers'
import { notifyError, notifySuccess } from 'utils'
import EnquiryStatusCell from 'shared/molecules/table/cells/EnquiryStatusCell'
import {
  Enquiries,
  EnquiryListRequest,
  UserEnquiry,
  EnquiryStatus
} from './types'
import {
  useGetAllEnquiriesQuery,
  useUpdateEnquiryApprovalMutation
} from './helpApiSlice'
import '../../scss/common/list/List.scss'
// eslint-disable-next-line import/order
import DescriptionCell from 'shared/molecules/table/cells/DescriptionCell'

const { Column, HeaderCell, Cell, ApproveCell } = Table
const COLUMNS = [
  { key: 'fullName', label: 'Name', width: 150 },
  { key: 'mobile', label: 'Number', width: 150 },
  { key: 'email', label: 'Email', width: 190 },
  { key: 'message', label: 'Description', width: 150 },
  { key: 'status', label: 'Status', Width: 170 }
]

const EnquiriesList: FC = () => {
  const [loading, setLoading] = useState<boolean>(false)
  const { requestBody, onPageChange, onSortColumn, onSearchChange } =
    useTableHandlers<Enquiries, EnquiryListRequest>(
      {
        page: { size: 10, number: 1 },
        filters: []
      },
      'fullname'
    )
  const [showDeleteModal, setShowApproveModal] = useState<boolean>(false)
  const [selectedEnquiry, setSelectedEnquiry] = useState<Enquiries | null>(null)
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const { data, isFetching } = useGetAllEnquiriesQuery(requestBody)
  const [updateUserEnquiry] = useUpdateEnquiryApprovalMutation()

  const handleSortColumn = useCallback(
    (column: string, type?: SortType) => {
      setLoading(true)
      setTimeout(() => {
        onSortColumn(column, type)
        setLoading(false)
      }, 500)
    },
    [onSortColumn]
  )
  const handleApproveClick = (rowData: Enquiries) => {
    setSelectedEnquiry(rowData)
    setShowApproveModal(true)
  }

  const handleConfirmApprove = async () => {
    if (!selectedEnquiry?.id) return
    setIsSubmitting(true)
    try {
      const payload: UserEnquiry = {
        id: Number(selectedEnquiry.id),
        status: EnquiryStatus.RESOLVED
      }

      await updateUserEnquiry({
        id: payload.id ?? 0,
        userEnquiry: payload
      }).unwrap()

      notifySuccess('Enquiry Status Updated Successfully!')
      setShowApproveModal(false)
    } catch (error) {
      notifyError('Failed to update Enquiry Status')
    } finally {
      setIsSubmitting(false)
    }
  }
  const options = [
    {
      label: 'Enquiry',
      value: 'all',
      onClick: () => {}
    }
  ]
  const renderCell = (key: string) => {
    if (key === 'status') {
      return <EnquiryStatusCell dataKey={key} />
    }
    if (key === 'message') {
      return <DescriptionCell dataKey={key} />
    }
    return <Cell dataKey={key} />
  }

  return (
    <div className="user-list">
      <Toolbar
        options={options}
        total={data?.totalElements ?? 0}
        onSearchChange={onSearchChange}
      />
      <div className="list__main-container">
        <Table
          data={data?.content ?? []}
          loading={loading || isFetching}
          paginated
          onSortColumn={handleSortColumn}
          pageSizeOptions={[10, 20, 30]}
          total={data?.totalElements ?? 0}
          defaultPageSize={10}
          onPageChange={onPageChange}
        >
          {COLUMNS.map((column, index) => {
            const { key, label } = column
            return (
              <Column
                flexGrow={1}
                key={key}
                align={index === 0 ? 'left' : 'center'}
                sortable
              >
                <HeaderCell>{label}</HeaderCell>
                {renderCell(key)}
              </Column>
            )
          })}
          <Column flexGrow={1} align="center" key="" sortable>
            <HeaderCell>Approve Request</HeaderCell>
            <ApproveCell onApproveClick={handleApproveClick} />
          </Column>
        </Table>
      </div>
      {showDeleteModal && (
        <Modal
          open={showDeleteModal}
          onClose={() => setShowApproveModal(false)}
          title="Confirm Update Enquiry"
          size="calc(40% - 120px)"
          className="delete-modal"
          body={
            <div className="modal-content">
              <p className="modal-text">
                Are you sure you want to update this enquiry status?
              </p>
              <div className="modal-buttons">
                <div>
                  <Button
                    onClick={() => setShowApproveModal(false)}
                    appearance="primary"
                    color="red"
                    className="no-btn"
                  >
                    No
                  </Button>
                </div>
                <div>
                  <Button
                    onClick={() => handleConfirmApprove()}
                    appearance="primary"
                    className="yes-btn"
                    disabled={isSubmitting}
                  >
                    Yes
                  </Button>
                </div>
              </div>
            </div>
          }
        />
      )}
    </div>
  )
}

export default EnquiriesList
